/**
 * sl-automation-refactor.4: AutomationConfigApiService
 *
 * API Client for Automation Config/Schedule management.
 * Communicates with Backend /api/workspace/:workspaceId/workflow/configs/* endpoints.
 *
 * SOLID:
 * - S: Single Responsibility - Only handles config API calls
 * - I: Interface Segregation - Focused API surface
 */

import type { AutomationExecutionKind } from '@streamlain/database-identity';
import { STEP_TYPES } from '@streamlain/field-types';
import { isWorkflowPublishRefusal } from '@streamlain/workflow-field-schemas';
import { LiveData, Service } from '@toeverything/infra';

import { defaultWorkspaceApiClient, WorkspaceApiError } from '../../cloud';
// sl-trigger-removal.5: Import from types instead of removed trigger service
import type { CronPreset, TriggerType } from '../types';
import { WorkflowPublishRefusedError } from './workflow-publish-refused-error';

// =============================================================================
// Types (matching Backend DTOs)
// =============================================================================

export interface ConfigResponse {
  id: string;
  workspaceId: string;
  docId: string;
  blockId: string;
  webhookUrl: string | null;
  executionKind: AutomationExecutionKind;
  status: 'active' | 'trashed';
  trashedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  permissionStatus: string;
  permissionError: string | null;
  schedule: ScheduleResponse | null;
}

/** The person a switched-on automation runs as. */
export interface AutomationRunsAs {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
}

/** A connection of a published step, seen by the reader. */
export interface AutomationStepConnection {
  stepId: string;
  connectionId: string;
  label: string | null;
  serviceDefinitionId: string | null;
  /** The reader may use its credential, so it stays after taking over. */
  usable: boolean;
}

/** A connection the reader may use instead of one of the same service. */
export interface AutomationOwnConnection {
  connectionId: string;
  label: string;
  serviceDefinitionId: string;
}

/** GET of one config: its response plus the readiness facts only it reads. */
export interface ConfigReadResponse extends ConfigResponse {
  /**
   * Another automatic automation on the source table writes the status
   * column this automatic automation would write.
   */
  statusColumnShared: boolean;
  runsAs: AutomationRunsAs | null;
  connections: AutomationStepConnection[];
  /** Credentials referenced in step settings the reader may not use. */
  deniedCredentialCount: number;
  ownConnections: AutomationOwnConnection[];
  /** New rows the import brake holds back until someone runs or discards them. */
  heldRows: number;
}

/** `code` of a change the server refuses until the automation is taken over. */
export const AUTOMATION_TAKEOVER_REQUIRED = 'AUTOMATION_TAKEOVER_REQUIRED';

/** The server refused a change because the automation runs as someone else. */
export function isAutomationTakeoverRequired(error: unknown): boolean {
  return (
    error instanceof WorkspaceApiError &&
    error.code === AUTOMATION_TAKEOVER_REQUIRED
  );
}

/** Overview row of one automation block (GET automation-summaries). */
export interface AutomationDocSummary {
  blockId: string;
  triggerType: TriggerType;
  enabled: boolean;
  pausedReason: string | null;
  lastRun: {
    status:
      | 'running'
      | 'awaiting_external'
      | 'completed'
      | 'failed'
      | 'cancelled';
    startedAt: string;
  } | null;
}

export interface ScheduleResponse {
  id: string;
  configId: string;
  triggerType: TriggerType;
  cronPreset: CronPreset | null;
  timezone: string;
  enabled: boolean;
  /** Why the server switched it off; null when on or switched off by a person. */
  pausedReason: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CleanupPreviewResponse {
  configId: string;
  configVersion: string;
  scheduleVersion: string;
  checkedAt: string;
  sampled: number;
  sample: Array<{
    id: string;
    cells: Record<string, { columnId: string; value: unknown }>;
  }>;
}

export interface ConfigInput {
  docId: string;
  executionKind?: AutomationExecutionKind;
  sourceTableId?: string;
  sourceDocId?: string;
  filterConfig?: unknown;
}

/**
 * ScheduleInput - Schedule configuration.
 *
 * The request persists schedule state. Y.js mirrors the same value for
 * local-first rendering.
 */
export interface ScheduleInput {
  triggerType: TriggerType;
  cronPreset?: CronPreset;
  timezone?: string;
  enabled?: boolean;
  /** The webhook a `webhook` trigger listens on. */
  webhookCredentialId?: string | null;
  expectedConfigVersion?: string;
  expectedScheduleVersion?: string;
}

/**
 * E029-PREFLIGHT: Config data from verify-sync endpoint.
 * Contains all execution-relevant attributes for field-by-field comparison.
 */
export interface VerifySyncConfig {
  executionKind?: AutomationExecutionKind;
  // Strings
  workflowId: string | null;
  sourceTableId: string | null;
  sourceDocId: string | null;
  // Deep objects
  outputTables: Array<{
    tableId: string;
    docId: string;
    mode?: string;
    matchConfigId?: string;
  }>;
  statusTracking: {
    enabled: boolean;
    mode?: string;
    columnId?: string;
    successValue: string;
  } | null;
  triggerConfig: {
    type?: string;
    enabled?: boolean;
    cronPreset?: string;
  } | null;
  batchConfig: {
    batchSize?: number;
    executionMode?: 'row' | 'batch';
  } | null;
  // Full objects (direct deep compare — no hashing)
  filterConfig: unknown | null;
  inputMappings: Array<{
    schemaFieldId: string;
    columnId: string | null;
    inputTableId?: string;
  }> | null;
  inputTables: Array<{
    id: string;
    tableId: string;
    tableName: string;
    docId?: string;
    role: 'lead' | 'secondary';
    filterGroup?: unknown;
    linkedVia?: string;
    cardinality?: 'one' | 'many';
    multiFieldName?: string;
  }> | null;
  outputMappings: unknown[] | null;
  matchConfigs: unknown[] | null;
  // Counts
  outputTablesCount: number;
  outputMappingsCount: number;
  selectedColumnsCount: number;
  inputMappingsCount: number;
  inputTablesCount: number;
  // Arrays
  selectedColumns: string[];
}

/**
 * E029-PREFLIGHT: Response from verify-sync endpoint.
 * Frontend compares this with local block props before execution.
 */
export interface VerifySyncResponse {
  verified: boolean;
  config: VerifySyncConfig | null;
  error?: string;
  errorKind?: VerifySyncErrorKind;
  status?: number;
}

export type VerifySyncErrorKind =
  | 'backend-unavailable'
  | 'permission-denied'
  | 'sync-not-ready';

const BACKEND_UNAVAILABLE_STATUSES = new Set([0, 500, 502, 503, 504]);

export function classifyVerifySyncFailure(
  status: number,
  error: string
): VerifySyncErrorKind {
  const normalized = error.toLowerCase();

  if (
    BACKEND_UNAVAILABLE_STATUSES.has(status) ||
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('load failed') ||
    normalized.includes('error occurred while trying to proxy') ||
    normalized.includes('backend unreachable')
  ) {
    return 'backend-unavailable';
  }

  if (status === 401 || status === 403) {
    return 'permission-denied';
  }

  return 'sync-not-ready';
}

export function isVerifySyncBackendUnavailable(
  response: VerifySyncResponse
): boolean {
  return response.errorKind === 'backend-unavailable';
}

export type WorkflowTestRunStatus = 'active' | 'expired' | 'superseded';
export type WorkflowTestStepResultStatus =
  | 'pending'
  | 'success'
  | 'failed'
  | 'suspended'
  | 'skipped';

export interface WorkflowTestRunData {
  id: string;
  workspaceId: string;
  automationDocId: string;
  automationBlockId: string;
  workflowId: string;
  sourceDocIds: string[];
  sourceTableId?: string | null;
  createdBy: string;
  automationConfigId?: string | null;
  workflowSnapshotVersion?: number | null;
  workflowSnapshotHash?: string | null;
  status: WorkflowTestRunStatus;
  inputPayload: unknown;
  sourceSummary?: unknown;
  validationSummary?: unknown;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

export interface WorkflowTestStepResultData {
  id: string;
  testRunId: string;
  workspaceId: string;
  workflowId: string;
  stepId: string;
  status: WorkflowTestStepResultStatus;
  inputData?: unknown;
  outputData?: unknown;
  error?: string | null;
  durationMs?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowTestRunResponse {
  run: WorkflowTestRunData;
  stepResults: WorkflowTestStepResultData[];
}

export interface WorkflowStepPreviewResponse {
  run: WorkflowTestRunData;
  stepResults: WorkflowTestStepResultData[];
  previewedStep?: WorkflowTestStepResultData | null;
}

// =============================================================================
// Service
// =============================================================================

export class AutomationConfigApiService extends Service {
  /**
   * Loading state
   */
  readonly isLoading$ = new LiveData<boolean>(false);

  /**
   * Error state
   */
  readonly error$ = new LiveData<string | null>(null);

  private request(
    workspaceId: string,
    input: string | URL,
    init: RequestInit = {}
  ): Promise<Response> {
    const path = input.toString();
    const method = init.method ?? 'GET';
    const isMutation = method !== 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId,
      path,
      init,
      category: 'workspace-api',
      key: `${method}:${path}`,
      mode: isMutation ? 'foreground' : 'background',
      priority: isMutation ? 'foreground' : 'visible',
    });
  }

  // ===========================================================================
  // Config CRUD
  // ===========================================================================

  /**
   * Trigger, schedule state and last run of every automation in a doc.
   * Throws on failure so the overview never shows a guessed status.
   */
  async getDocSummaries(
    workspaceId: string,
    docId: string
  ): Promise<AutomationDocSummary[]> {
    const params = new URLSearchParams({ docId });
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/automation-summaries?${params}`
    );
    return response.json();
  }

  /**
   * Get config for a block
   */
  async getConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<ConfigReadResponse | null> {
    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}?${params}`
      );

      return await response.json();
    } catch (error) {
      console.error('[ConfigApi] getConfig error:', error);
      return null;
    }
  }

  /**
   * Create or update config for a block; null when it did not succeed. A
   * refusal until the automation is taken over is thrown, so the caller can
   * offer the take-over.
   */
  async saveConfig(
    blockId: string,
    workspaceId: string,
    input: ConfigInput
  ): Promise<ConfigResponse | null> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        }
      );

      return await response.json();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[ConfigApi] saveConfig error:', message);
      this.error$.next(message);
      if (isAutomationTakeoverRequired(error)) throw error;
      return null;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * The automation runs as the caller from now on, with `connections` in
   * place of the connections of the same steps. Throws the server's refusal.
   */
  async takeOver(
    blockId: string,
    workspaceId: string,
    docId: string,
    connections: ReadonlyArray<{ stepId: string; connectionId: string }>
  ): Promise<ConfigReadResponse> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/take-over`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, connections }),
      }
    );
    return response.json();
  }

  /** Asks the administrators of a connection's credential to share it. */
  async requestCredentialShare(
    blockId: string,
    workspaceId: string,
    docId: string,
    connectionId: string
  ): Promise<void> {
    await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/take-over/share-request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, connectionId }),
      }
    );
  }

  async previewCleanup(
    blockId: string,
    workspaceId: string,
    input: {
      docId: string;
      expectedConfigVersion: string;
      expectedScheduleVersion: string;
    }
  ): Promise<CleanupPreviewResponse> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/cleanup-preview`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }
    );
    return response.json();
  }

  // ===========================================================================
  // Schedule CRUD
  // ===========================================================================

  /**
   * Create or update schedule for a config. A refused or failed request
   * throws with the server's answer, so the caller can name the reason.
   */
  async requestSchedule(
    blockId: string,
    workspaceId: string,
    docId: string,
    input: ScheduleInput
  ): Promise<ScheduleResponse> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const params = new URLSearchParams({ docId });
      const response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/schedule?${params}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        }
      );

      return await response.json();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[ConfigApi] saveSchedule error:', message);
      this.error$.next(message);
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Create or update schedule for a config; null when it did not succeed.
   */
  async saveSchedule(
    blockId: string,
    workspaceId: string,
    docId: string,
    input: ScheduleInput
  ): Promise<ScheduleResponse | null> {
    try {
      return await this.requestSchedule(blockId, workspaceId, docId, input);
    } catch (error) {
      if (isAutomationTakeoverRequired(error)) throw error;
      return null;
    }
  }

  // ===========================================================================
  // E029-PREFLIGHT: Pre-Flight Sync Verification
  // ===========================================================================

  /**
   * E029-PREFLIGHT: Verify that the backend can read the block's Y.js props.
   *
   * Returns the config as the backend sees it, so the frontend can compare
   * with its local state before triggering execution.
   */
  async verifySyncConfig(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<VerifySyncResponse> {
    let response: Response;
    try {
      response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/verify-sync`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ docId }),
        }
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Verify request failed';
      return {
        verified: false,
        config: null,
        error: message,
        errorKind: classifyVerifySyncFailure(0, message),
        status: 0,
      };
    }

    return await response.json();
  }

  // ===========================================================================
  // Workflow Test Runs
  // ===========================================================================

  async createWorkflowTestRun(
    blockId: string,
    workspaceId: string,
    docId: string
  ): Promise<WorkflowTestRunResponse> {
    const params = new URLSearchParams({ docId });
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/test-runs?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return await response.json();
  }

  async createAdHocWorkflowTestRun(
    workspaceId: string,
    workflowId: string,
    inputData: Record<string, unknown>
  ): Promise<WorkflowTestRunResponse> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/test-runs/ad-hoc`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflowId, inputData }),
      }
    );

    return await response.json();
  }

  async getLatestWorkflowTestRun(
    blockId: string,
    workspaceId: string,
    workflowId?: string | null
  ): Promise<WorkflowTestRunResponse | null> {
    const params = new URLSearchParams();
    if (workflowId) params.set('workflowId', workflowId);
    let response: Response;
    try {
      response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/test-runs/latest?${params}`
      );
    } catch (error) {
      if (error instanceof WorkspaceApiError && error.status === 404)
        return null;
      throw error;
    }

    return await response.json();
  }

  async previewWorkflowStep(
    blockId: string,
    workspaceId: string,
    docId: string,
    testRunId: string,
    stepId: string,
    previewExecutionId: string
  ): Promise<WorkflowStepPreviewResponse> {
    const params = new URLSearchParams({ docId });
    let response: Response;
    try {
      response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/configs/${blockId}/test-runs/${testRunId}/preview-step?${params}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stepId,
            previewExecutionId,
            allowedStepTypes: STEP_TYPES,
          }),
        }
      );
    } catch (error) {
      if (
        error instanceof WorkspaceApiError &&
        isWorkflowPublishRefusal(error.responseBody)
      ) {
        throw new WorkflowPublishRefusedError(
          error.responseBody.message,
          error.responseBody.issues
        );
      }
      throw error;
    }

    return await response.json();
  }

  async previewAdHocWorkflowStep(
    workspaceId: string,
    workflowId: string,
    testRunId: string,
    stepId: string,
    previewExecutionId: string
  ): Promise<WorkflowStepPreviewResponse> {
    let response: Response;
    try {
      response = await this.request(
        workspaceId,
        `/api/workspace/${workspaceId}/workflow/test-runs/${testRunId}/preview-step`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workflowId,
            stepId,
            previewExecutionId,
            allowedStepTypes: STEP_TYPES,
          }),
        }
      );
    } catch (error) {
      if (
        error instanceof WorkspaceApiError &&
        isWorkflowPublishRefusal(error.responseBody)
      ) {
        throw new WorkflowPublishRefusedError(
          error.responseBody.message,
          error.responseBody.issues
        );
      }
      throw error;
    }

    return await response.json();
  }

  // ===========================================================================
  // sl-kpg5.27: AI Key Availability Check
  // ===========================================================================

  /**
   * sl-kpg5.27: Check if AI providers used by the workflow have API keys configured.
   * Returns which providers are used and which are missing keys.
   */
  async checkAiKeys(
    blockId: string,
    workspaceId: string
  ): Promise<{ usesAi: boolean; providers: string[]; missingKeys: string[] }> {
    const response = await this.request(
      workspaceId,
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/ai-key-check`
    );
    return await response.json();
  }
}
